// frontend/src/components/DataTable.js
import React, { useState, useMemo } from 'react';
import './DataTable.css';

// Tag imod nye props: initialSortKey og initialSortDirection
const DataTable = ({ 
  columns, 
  data, 
  loading, 
  noDataMessage = "Ingen data fundet.",
  initialSortKey = null,       // Default: Ingen sortering
  initialSortDirection = 'asc' // Default: Stigende (A-Å)
}) => {
  // State til sortering
  const [sortConfig, setSortConfig] = useState({ 
    key: initialSortKey, 
    direction: initialSortDirection 
  });

  // Håndter klik på header
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sorterings-logik (Kører hver gang data eller sortConfig ændres)
  const sortedData = useMemo(() => {
    let sortableItems = [...data]; // Kopier arrayet så vi ikke muterer originalen
    
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        // Hent værdierne baseret på accessor nøglen
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Håndter null/undefined (læg dem i bunden eller toppen)
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        // Hvis det er tekst, gør det lowerCase for korrekt sortering
        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();

        // Sammenlign
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [data, sortConfig]);

  if (loading) {
    return <div className="data-table-message">Henter data...</div>;
  }

  return (
    <table className="data-table">
      <thead>
        <tr>
          {columns.map((col, index) => {
            // Vi kan kun sortere hvis kolonnen har en 'accessor' (en nøgle til data)
            // Hvis det er en ren knap-kolonne (kun render), kan vi ikke sortere
            const isSortable = !!col.accessor; 
            
            return (
              <th 
                key={index} 
                style={{ 
                  width: col.width, 
                  textAlign: col.align || 'left',
                  cursor: isSortable ? 'pointer' : 'default' // Vis hånd-markør hvis sorterbar
                }}
                onClick={() => isSortable ? handleSort(col.accessor) : null}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start' }}>
                  {col.header}
                  
                  {/* Vis pil hvis denne kolonne er aktiv */}
                  {isSortable && sortConfig.key === col.accessor && (
                    <span className="sort-arrow">
                      {sortConfig.direction === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                  
                  {/* Vis en grå "inaktiv" pil hvis musen er over (valgfrit, kræver mere CSS) */}
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {sortedData.length > 0 ? (
          sortedData.map((row, rowIndex) => (
            <tr key={row.id || rowIndex}>
              {columns.map((col, colIndex) => (
                <td 
                  key={colIndex} 
                  style={{ textAlign: col.align || 'left' }}
                >
                  {col.render ? col.render(row) : row[col.accessor]}
                </td>
              ))}
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={columns.length} className="data-table-message">
              {noDataMessage}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default DataTable;